import { resolveApiBase } from "@/lib/config";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  const res = await fetch(`${resolveApiBase()}${path}`, {
    cache: "no-store",
    ...init,
    headers,
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = typeof body?.detail === "string" ? body.detail : "";
    } catch {
      /* ignore */
    }
    if (res.status === 401 && typeof window !== "undefined") {
      if (!window.location.pathname.startsWith("/login")) {
        // 会话失效：整页跳登录页，replace 避免后退回到需登录的页面
        window.location.replace("/login");
      }
      throw new Error(detail || "未登录或会话已过期");
    }
    throw Object.assign(new Error(detail || `API ${path} 请求失败: ${res.status}`), { status: res.status });
  }
  return res.json();
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export interface Course {
  id: string;
  title: string;
  description: string;
  source: string;
  version: string;
  status: string;
}

export interface Example {
  id: string;
  english: string;
  chinese: string;
  analysis: string;
}

export interface DerivationDemo {
  title?: string;
  note?: string;
  directions?: {
    label?: string;
    original?: string;
    steps?: { op?: string; change?: string; reason?: string }[];
    result?: string;
  }[];
  insight?: string;
}

export interface Exercise {
  id: string;
  type: string;
  question: string;
  options: string[];
  answer: string;
  explanation: string;
}

export type CoreRuleBlock =
  | { kind: "text"; text: string }
  | { kind: "formula"; text: string; caption?: string }
  | { kind: "points"; title?: string; items: string[] }
  | { kind: "steps"; title?: string; items: string[] }
  | { kind: "example"; title?: string; text: string };

export interface MapConcept {
  id: string;
  title: string;
  summary: string;
  formula: string;
  points: string[];
}

export interface MapSection {
  title: string;
  concepts: MapConcept[];
}

export interface MapChapter {
  id: string;
  number: number;
  title: string;
  sections: MapSection[];
}

export interface MapGroup {
  label: string;
  chapters: MapChapter[];
}

export interface GrammarMap {
  root: string;
  total: number;
  groups: MapGroup[];
}

export interface TransformationItem {
  lead: string;
  requirement: string;
  gapped: string;
  answer: string;
}

export interface Concept {
  id: string;
  section_id: string;
  title: string;
  type: string;
  summary: string;
  core_rule: string;
  core_rule_blocks?: CoreRuleBlock[];
  why: string;
  common_mistakes: string;
  derivation?: DerivationDemo[];
  transformations?: TransformationItem[];
  difficulty: number;
  order: number;
  examples: Example[];
  exercises: Exercise[];
  mastery?: {
    score: number;
    status: string;
    review_count: number;
    wrong_count: number;
    last_activity_at?: string | null;
    status_source?: string;
  } | null;
}

export type ConceptSummary = Pick<
  Concept,
  "id" | "section_id" | "title" | "type" | "summary" | "difficulty" | "order"
> & {
  mastery?: Concept["mastery"];
};

export interface Section {
  id: string;
  chapter_id: string;
  title: string;
  summary: string;
  order: number;
  concept_count?: number;
  mastered_count?: number;
  concept_progress?: number;
}

export interface SectionDiagramNode {
  label: string;
  note?: string;
  concept_id?: string;
  children?: SectionDiagramNode[];
}

export interface SectionDiagram {
  root: string;
  children: SectionDiagramNode[];
}

export interface SectionDetail extends Section {
  concepts: ConceptSummary[];
  diagram?: SectionDiagram | null;
}

export interface Chapter {
  id: string;
  course_id: string;
  number: number;
  title: string;
  description: string;
  order: number;
}

export interface ChapterDetail extends Chapter {
  sections: Section[];
  exercises: Exercise[];
}

export interface MasterySummary {
  total_concepts: number;
  mastered: number;
  learning: number;
  weak: number;
  new: number;
  weak_concepts: {
    concept_id: string;
    title: string;
    score: number;
    status: string;
  }[];
}

export interface ContinueTarget {
  type: "learn" | "review" | "done";
  concept_id: string | null;
  title: string;
  section_id: string | null;
  chapter?: { id: string; number: number; title: string } | null;
  reason: string;
  recommendation_reason?: { label: string; value: string }[];
}

export interface NavNode {
  concept_id: string;
  title: string;
  section_id: string;
  section_title: string;
  chapter: { id: string; number: number; title: string; course_id?: string } | null;
}

export interface ConceptNav {
  prev: NavNode | null;
  next: NavNode | null;
  current?: NavNode;
  total: number;
  index: number;
  course_id?: string;
}

export interface ReviewSummary {
  total: number;
  weak: number;
  mastered: number;
  learning?: number;
}

export interface ReviewQueue {
  items: {
    concept_id: string;
    title: string;
    status: string;
    score: number;
    due_reason: string;
    last_activity_at?: string | null;
    recommended_today?: boolean;
    state_token?: string;
    status_source?: string;
    status_updated_at?: string | null;
    chapter_id?: string;
    chapter_number?: number;
    chapter_title?: string;
    section_title?: string;
  }[];
  total: number;
  has_studied: boolean;
  summary?: ReviewSummary;
  today_date?: string;
  completed_today?: string[];
  schedule_rule?: string;
}

export interface CatalogConcept {
  id: string;
  title: string;
  type: string;
  difficulty: number;
  order: number;
  mastery_status: string;
  mastery_score: number;
}

export interface CatalogSection {
  id: string;
  title: string;
  order: number;
  concepts: CatalogConcept[];
}

export interface CatalogChapter {
  id: string;
  number: number;
  title: string;
  order: number;
  sections: CatalogSection[];
}

export interface Catalog {
  course_id: string;
  course_title: string;
  chapters: CatalogChapter[];
}

export interface QuizQuestion {
  question: string;
  options: string[];
  answer: string;
  explanation: string;
}

export interface Quiz {
  concept_id: string;
  concept_title: string;
  questions: QuizQuestion[];
}

export interface AIQuiz {
  concept_id: string;
  concept_title: string;
  questions: QuizQuestion[];
}

export interface QuizSessionQuestion {
  exercise_id: string;
  question: string;
  options: string[];
  type: string;
  chapter: string;
  difficulty: number;
}

export interface QuizSession {
  session_id: number;
  total: number;
  questions: QuizSessionQuestion[];
}

export interface QuizResultItem {
  exercise_id: string;
  answer: string;
  correct: boolean;
  correct_answer: string;
  explanation: string;
}

export interface QuizResult {
  correct: number;
  total: number;
  results: QuizResultItem[];
}

export interface PracticeAnalysis {
  sentence_skeleton?: string;
  decision_steps?: string[];
  correct_reason?: string;
  distractor_reasons?: Record<string, string>;
}

export interface PracticeQuestionResponse {
  answer: string;
  correct: boolean | null;
  duration: number;
  flagged: boolean;
  hint_level: number;
  assisted: boolean;
  assistance_credit: "pending" | "none" | "independent" | "light_assisted" | "scaffolded" | "bottom_out" | "wrong";
}

export interface PracticeHint {
  level: number;
  hint_type: "cue" | "scaffold" | "explanation" | "bottom_out";
  content: string;
  is_bottom_out: boolean;
}

export interface PracticeQuestion {
  id: string;
  module: string;
  module_label: string;
  primary_concept_id: string;
  concept_title: string;
  difficulty: string;
  question_type: string;
  stem: string;
  options: Record<string, string>;
  answer_key?: string | null;
  analysis?: PracticeAnalysis | null;
  diagnostic_tags: string[];
  response?: PracticeQuestionResponse | null;
  hint_available: boolean;
  revealed_hints: PracticeHint[];
}

export interface PracticeSession {
  session_id: number;
  chapter_number: number;
  chapter_title: string;
  mode: "fragment" | "chapter" | "test" | "wrong";
  mode_label: string;
  status: "active" | "completed";
  blueprint_version: string;
  selector_version: string;
  selection_summary: { adaptive?: boolean; unseen?: number; review?: number };
  current_index: number;
  total: number;
  answered: number;
  correct: number;
  duration: number;
  questions: PracticeQuestion[];
}

export interface PracticeChapterSummary {
  chapter_number: number;
  title: string;
  question_count: number;
  seen_count: number;
  completed_sessions: number;
  best_accuracy: number;
  diagnosis: {
    snapshot_id: number;
    created_at: string;
    summary: PracticeDiagnosisSummary;
  } | null;
}

export interface PracticeHistoryItem {
  session_id: number;
  chapter_number: number;
  chapter_title: string;
  mode: string;
  mode_label: string;
  correct: number;
  total: number;
  accuracy: number;
  duration: number;
  submitted_at: string;
}

export interface PracticeLoopStage {
  stage_id: string;
  label: string;
  index: number;
  modules: string[];
  independent_attempts: number;
  independent_correct: number;
  independent_accuracy: number;
  required_independent_attempts: number;
  module_independent_attempts: Record<string, number>;
  module_coverage_complete: boolean;
  passed: boolean;
}

export interface PracticeLoopModule {
  module: string;
  label: string;
  stage_id: string;
  stage_label: string;
  independent_attempts: number;
  independent_correct: number;
  independent_accuracy: number;
  target: number;
  ready: boolean;
}

export interface PracticeLoopChapter {
  chapter_number: number;
  question_count: number;
  graded_events: number;
  seen_questions: number;
  independent_attempts: number;
  independent_correct: number;
  independent_accuracy: number;
  assisted_attempts: number;
  assistance_rate: number;
  wrong_events: number;
  unresolved_wrong_questions: number;
  due_concepts: number;
  evidence_concepts: number;
  completed_standard_tests: number;
  diagnosis_snapshots: number;
  current_stage: { stage_id: string; label: string; index: number; total: number };
  stages: PracticeLoopStage[];
  modules: PracticeLoopModule[];
  top_error_tags: { tag: string; count: number }[];
  calibration_ready: boolean;
  readiness_progress: number;
  next_action: {
    mode: "fragment" | "test" | "wrong";
    count: number;
    label: string;
    reason: string;
  };
}

export interface PracticeLoopInsights {
  version: string;
  generated_at: string;
  model_version: string;
  thresholds: {
    graded_events: number;
    standard_tests: number;
    module_independent_attempts: number;
  };
  total_graded_events: number;
  total_evidence_concepts: number;
  hint_coverage: Record<string, number>;
  chapters: PracticeLoopChapter[];
  integrity: {
    duplicate_graded_pairs: number;
    completed_responses_without_graded_event: number;
    graded_events_without_concepts: number;
    healthy: boolean;
  };
}

export interface PracticeOverview {
  chapters: PracticeChapterSummary[];
  learning_loop: PracticeLoopInsights;
  active_session: PracticeSession | null;
  recent_sessions: PracticeHistoryItem[];
}

export interface PracticeReport {
  session_id: number;
  chapter_number: number;
  chapter_title: string;
  mode: string;
  mode_label: string;
  status: string;
  total: number;
  answered: number;
  correct: number;
  accuracy: number;
  duration: number;
  selector_version: string;
  selection_metadata: Record<string, unknown>;
  evidence_summary: {
    answered: number;
    total: number;
    independent_correct: number;
    assisted_correct: number;
    wrong: number;
    independent_accuracy: number;
    assistance_rate: number;
    hint_levels: Record<string, number>;
  };
  modules: { module: string; label: string; total: number; correct: number; accuracy: number }[];
  weak_concepts: {
    concept_id: string;
    title: string;
    total: number;
    correct: number;
    accuracy: number;
    confidence: number;
    evidence_status: string;
  }[];
  wrong_questions: PracticeQuestion[];
  diagnosis: PracticeDiagnosis | null;
  submitted_at: string;
}

export interface PracticeDiagnosisSummary {
  version: string;
  session_id: number;
  chapter_number: number;
  surface_accuracy: number;
  independent_accuracy: number;
  assistance_rate: number;
  answered: number;
  total: number;
  modules: {
    module: string;
    label: string;
    total: number;
    independent_correct: number;
    independent_accuracy: number;
  }[];
  priorities: {
    concept_id: string;
    title: string;
    total: number;
    independent_correct: number;
    independent_accuracy: number;
    confidence: number;
    status: string;
  }[];
  top_error_tags: { tag: string; count: number }[];
  comparison: {
    previous_snapshot_id: number | null;
    previous_independent_accuracy: number | null;
    independent_accuracy_delta: number | null;
  };
  recommendation: { mode: "wrong" | "fragment"; count: number; focus_concept_ids: string[]; reason: string };
}

export interface PracticeDiagnosis {
  snapshot_id: number;
  status: string;
  version: string;
  summary: PracticeDiagnosisSummary;
  narrative: string;
  narrative_source: string;
  created_at: string;
}

export interface AttemptResult {
  correct: boolean;
  answer: string;
  correct_answer: string;
  explanation: string;
}

export interface AITutorMessage {
  role: "user" | "ai";
  content: string;
  action?: string;
}

export interface AISessionMeta {
  id: number;
  title: string;
  message_count: number;
}

export interface UsageSummary {
  total_calls: number;
  cached_calls: number;
  llm_calls: number;
  total_tokens: number;
  total_cost: number;
  by_action: { action: string; model: string; calls: number; cached: number; tokens: number; cost: number }[];
}

export interface LongSentence {
  id: number;
  source: string;
  source_url: string;
  sentence: string;
  translation: string;
  analysis: string;
  grammar_tags: string[];
  grammar_titles?: string[];
  difficulty: number;
}

export interface SessionCompleteBody {
  recall_done: boolean;
  recall_attempt_id?: number | null;
  recall_self_ok?: boolean | null;
  practice_correct: number;
  practice_total: number;
  transfer_done: boolean;
}

export interface SessionCompleteResult {
  ok: boolean;
  concept_id: string;
  mastery_score: number;
  mastery_status: string;
  message: string;
  today_completed?: number;
  streak?: number;
  section_mastered?: number;
  section_total?: number;
}

export interface NoteData {
  id: number;
  page_id: string;
  content: string;
  concept_id?: string | null;
  section_id?: string | null;
  chapter_id?: string | null;
  title?: string;
  created_at: string;
  updated_at: string;
}

export interface NoteListItem extends NoteData {
  chapter_title: string;
  section_title: string;
  concept_title: string;
}

export interface RecallFeedback {
  summary: string;
  score: number;
  verdict: string;
  covered: string[];
  missed: string[];
  wrong: string[];
  focus: string[];
  dimensions?: {
    core_logic: number;
    key_rule: number;
    logic_gaps: string[];
    rule_gaps: string[];
  };
  highlights?: { quote: string; praise: string; expansion: string }[];
  history_note?: string;
}

export interface RecallEvalResult {
  attempt_id: number;
  concept_id: string;
  input_text: string;
  feedback: RecallFeedback;
  created_at: string;
}

export interface RecallAttempt {
  id: number;
  concept_id: string;
  input_text: string;
  score: number;
  verdict: string;
  feedback_json: string;
  skipped: boolean;
  created_at: string;
}

export interface ConceptMark {
  id: number;
  concept_id: string;
  container_id: string;
  quote: string;
  created_at: string;
}

export interface ArticleItem {
  id: number;
  title: string;
  level: number;
  chapter_id: string | null;
  estimated_minutes: number;
  sentence_count: number;
  grammar_points: string[];
  read: boolean;
  quiz_score: number;
  quiz_total: number;
}

export interface ArticleDetail {
  id: number;
  title: string;
  level: number;
  content: string;
  estimated_minutes: number;
  source: string;
  sentences: {
    idx: number;
    text: string;
    translation: string;
    analysis: string;
    concept_id: string;
  }[];
  grammar_points: string[];
  diagram?: {
    title?: string;
    groups?: { label: string; items: string[] }[];
    mapping?: { component: string; roles: string; example: string }[];
  };
  read: boolean;
  quiz_score: number;
  quiz_total: number;
}

export interface ProgressSummary {
  today_completed: number;
  streak: number;
  total_completed: number;
  total_concepts: number;
  last_7_days: { date: string; count: number }[];
  heatmap?: {
    chapter_id: string;
    number: number;
    title: string;
    mastered: number;
    learning: number;
    weak: number;
    new: number;
  }[];
  timeline?: {
    type: "learn" | "recall";
    title: string;
    time: string;
    score: number | null;
    verdict: string;
    note: string;
  }[];
}

export function createApi(
  requestImpl: <T>(path: string, init?: RequestInit) => Promise<T>,
  postImpl: <T>(path: string, body?: unknown) => Promise<T>,
) {
  return {
    login: (username: string, password: string) =>
      postImpl<{ ok: boolean; username: string; user_id: string }>("/auth/login", { username, password }),
    logout: () => postImpl<{ ok: boolean }>("/auth/logout"),
    me: () => requestImpl<{ ok: boolean; username: string; user_id: string }>("/auth/me"),
    courses: () => requestImpl<Course[]>("/courses"),
    course: (id: string) => requestImpl<Course>(`/courses/${id}`),
    catalog: (courseId: string) => requestImpl<Catalog>(`/courses/${courseId}/catalog`),
    chapter: (id: string) => requestImpl<ChapterDetail>(`/chapters/${id}`),
    section: (id: string) => requestImpl<SectionDetail>(`/sections/${id}`),
    concept: (id: string) => requestImpl<Concept>(`/concepts/${id}`),
    conceptNav: (id: string) => requestImpl<ConceptNav>(`/concepts/${id}/nav`),
    mastery: () => requestImpl<MasterySummary>("/masteries"),
    reviews: () => requestImpl<ReviewQueue>("/reviews"),
    continueLearning: () => requestImpl<ContinueTarget>("/continue"),
    assessReview: (conceptId: string, status: "weak" | "mastered", expectedState: string, requestId: string) =>
      postImpl<{ ok: boolean; adjustment_id: number }>(`/reviews/${conceptId}/assessment`, {
        status,
        expected_state: expectedState,
        request_id: requestId,
      }),
    undoAssessment: (id: number) => postImpl<{ ok: boolean }>(`/reviews/adjustments/${id}/undo`),
    completeReview: (conceptId: string) => postImpl<{ ok: boolean }>(`/reviews/${conceptId}/complete`),
    attempt: (exerciseId: string, answer: string, duration = 0, clientAttemptId?: string) =>
      postImpl<AttemptResult>("/attempts", {
        exercise_id: exerciseId,
        answer,
        duration,
        client_attempt_id: clientAttemptId,
      }),
    quizStart: (count: number) => requestImpl<QuizSession>(`/quiz/start?count=${count}`),
    quizSubmit: (sessionId: number, answers: { exercise_id: string; answer: string }[]) =>
      postImpl<QuizResult>("/quiz/submit", { session_id: sessionId, answers }),
    practiceOverview: () => requestImpl<PracticeOverview>("/practice/overview"),
    practiceInsights: () => requestImpl<PracticeLoopInsights>("/practice/insights"),
    practiceStart: (chapterNumber: number, mode: PracticeSession["mode"], count: number) =>
      postImpl<PracticeSession>("/practice/sessions", { chapter_number: chapterNumber, mode, count }),
    practiceSession: (sessionId: number) => requestImpl<PracticeSession>(`/practice/sessions/${sessionId}`),
    practiceAnswer: (sessionId: number, questionId: string, answer: string, duration: number, flagged = false) =>
      postImpl<{ saved: boolean; question: PracticeQuestion }>(`/practice/sessions/${sessionId}/responses`, {
        question_id: questionId,
        answer,
        duration,
        flagged,
      }),
    practiceHint: (sessionId: number, questionId: string) =>
      postImpl<{ hint: PracticeHint; has_more: boolean }>(
        `/practice/sessions/${sessionId}/questions/${questionId}/hints/next`,
      ),
    practiceSubmit: (sessionId: number) => postImpl<PracticeReport>(`/practice/sessions/${sessionId}/submit`),
    practiceReport: (sessionId: number) => requestImpl<PracticeReport>(`/practice/sessions/${sessionId}/report`),
    practiceDiagnosisNarrative: (snapshotId: number) =>
      postImpl<{ snapshot_id: number; narrative: string; narrative_source: string }>(
        `/practice/diagnoses/${snapshotId}/narrative`,
      ),
    practiceDiagnosisReset: (chapterNumber: number) =>
      postImpl<{ ok: boolean; chapter_number: number; reset_count: number }>("/practice/diagnoses/reset", {
        chapter_number: chapterNumber,
      }),
    practiceQuestionAsk: (sessionId: number, questionId: string, message: string) =>
      postImpl<{ content: string; model: string }>(`/practice/sessions/${sessionId}/questions/${questionId}/ask`, {
        message,
      }),
    aiQuiz: (conceptId: string, count: number) => postImpl<AIQuiz>("/ai/quiz", { concept_id: conceptId, count }),
    aiSessions: (conceptId: string) => requestImpl<AISessionMeta[]>(`/ai/sessions/${conceptId}`),
    aiSessionMessages: (sessionId: number) => requestImpl<AITutorMessage[]>(`/ai/sessions/${sessionId}/messages`),
    aiUsage: () => requestImpl<UsageSummary>("/ai/usage"),
    aiDeleteSession: (sessionId: number) =>
      requestImpl<{ ok: boolean }>(`/ai/sessions/${sessionId}`, { method: "DELETE" }),
    longSentences: (conceptId: string) =>
      requestImpl<{ total: number; sentences: LongSentence[] }>(`/long-sentences?concept_id=${conceptId}&limit=20`),
    completeSession: (conceptId: string, body: SessionCompleteBody) =>
      postImpl<SessionCompleteResult>(`/concepts/${conceptId}/session/complete`, body),
    note: (pageId: string) => requestImpl<NoteData>(`/notes/${pageId}`),
    saveNote: (
      pageId: string,
      content: string,
      context?: {
        concept_id?: string;
        section_id?: string;
        chapter_id?: string;
        title?: string;
        base_updated_at?: string;
      },
    ) =>
      requestImpl<NoteData>(`/notes/${pageId}`, {
        method: "PUT",
        body: JSON.stringify({ page_id: pageId, content, ...context }),
      }),
    notes: () => requestImpl<NoteListItem[]>("/notes"),
    deleteNote: (pageId: string) => requestImpl<{ ok: boolean }>(`/notes/${pageId}`, { method: "DELETE" }),
    recallEvaluate: (conceptId: string, inputText: string) =>
      postImpl<RecallEvalResult>("/ai/recall/evaluate", { concept_id: conceptId, input_text: inputText }),
    recallSkip: (conceptId: string) => postImpl<RecallAttempt>("/ai/recall/skip", { concept_id: conceptId }),
    recallHistory: (conceptId: string) => requestImpl<RecallAttempt[]>(`/ai/recall/history/${conceptId}`),
    conceptMarks: (conceptId: string) =>
      requestImpl<ConceptMark[]>(`/marks?concept_id=${encodeURIComponent(conceptId)}`),
    addConceptMark: (conceptId: string, containerId: string, quote: string) =>
      postImpl<ConceptMark>("/marks", { concept_id: conceptId, container_id: containerId, quote }),
    clearConceptMarks: (conceptId: string) =>
      requestImpl<{ ok: boolean }>(`/marks?concept_id=${encodeURIComponent(conceptId)}`, { method: "DELETE" }),
    translate: (q: string) => requestImpl<{ q: string; translation: string }>(`/translate?q=${encodeURIComponent(q)}`),
    articles: () => requestImpl<ArticleItem[]>("/articles"),
    article: (id: number) => requestImpl<ArticleDetail>(`/articles/${id}`),
    articleComplete: (id: number, quizScore: number, quizTotal: number) =>
      postImpl<{ ok: boolean }>(`/articles/${id}/complete`, { quiz_score: quizScore, quiz_total: quizTotal }),
    progressSummary: () => requestImpl<ProgressSummary>("/progress/summary"),
    grammarMap: () => requestImpl<GrammarMap>("/grammar-map"),
  };
}

export const api = createApi(request, post);
